import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Image as ImageIcon, 
  Video, 
  Download, 
  Copy, 
  Share2, 
  Trash2,
  Filter,
  Search,
  Grid3X3,
  List,
  Calendar,
  Clock,
  Play,
  Pause,
  Heart,
  Star,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { downloadFile, copyToClipboard, shareContent, formatDate } from '../utils';
import { cn } from '../utils';
import { getUserImages, toggleImageFavorite, deleteUserImage } from '../services/gallery';

const filterOptions = [
  { id: 'all', name: 'Tutti', icon: Grid3X3 },
  { id: 'image', name: 'Immagini', icon: ImageIcon },
  { id: 'video', name: 'Video', icon: Video },
  { id: 'liked', name: 'Preferiti', icon: Heart }
];

const sortOptions = [
  { id: 'newest', name: 'Più recenti' },
  { id: 'oldest', name: 'Più vecchi' },
  { id: 'rating', name: 'Valutazione' }
];

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedItem, setSelectedItem] = useState(null);
  const [playingVideos, setPlayingVideos] = useState(new Set());
  const [selectedItems, setSelectedItems] = useState(new Set());

  // Carica le immagini dal database
  const loadUserImages = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getUserImages();
      
      if (!result.success) {
        throw new Error(result.error || 'Errore nel recupero delle immagini');
      }
      
      // Trasforma i dati per adattarli al formato del componente
      const formattedImages = result.images.map(item => ({
        id: item.id,
        type: 'image',
        url: item.image_url,
        thumbnail: item.image_url,
        prompt: item.prompt,
        model: item.model,
        timestamp: new Date(item.created_at).getTime(),
        size: '2.4 MB', // Placeholder
        dimensions: item.image_size || '1024x1024',
        liked: item.is_favorite || false, // Usa il valore dal database
        rating: 4.5
      }));
      
      setItems(formattedImages);
    } catch (err) {
      console.error('Errore nel caricamento delle immagini:', err);
      setError(err.message || 'Errore nel caricamento delle immagini. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  // Carica le immagini al mount del componente
  useEffect(() => {
    loadUserImages();
  }, []);

  // Applica filtri, ricerca e ordinamento
  useEffect(() => {
    let filtered = [...items];

    // Applica filtri
    if (selectedFilter === 'image') {
      filtered = filtered.filter(item => item.type === 'image');
    } else if (selectedFilter === 'video') {
      filtered = filtered.filter(item => item.type === 'video');
    } else if (selectedFilter === 'liked') {
      filtered = filtered.filter(item => item.liked);
    }

    // Applica ricerca
    if (searchQuery) {
      filtered = filtered.filter(item => 
        item.prompt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.model.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Applica ordinamento
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return b.timestamp - a.timestamp;
        case 'oldest':
          return a.timestamp - b.timestamp;
        case 'rating':
          return b.rating - a.rating;
        default:
          return 0;
      }
    });

    setFilteredItems(filtered);
  }, [items, selectedFilter, sortBy, searchQuery]);

  // Aggiorna la funzione toggleLike per usare l'API
  const toggleLike = async (itemId) => {
    try {
      // Trova l'item corrente
      const currentItem = items.find(item => item.id === itemId);
      if (!currentItem) return;

      const newLikedState = !currentItem.liked;
      
      // Aggiorna immediatamente l'UI (optimistic update)
      setItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, liked: newLikedState } : item
      ));

      // Chiama l'API per aggiornare il database
      const result = await toggleImageFavorite(itemId, newLikedState);
      
      if (!result.success) {
        // Se l'API fallisce, ripristina lo stato precedente
        setItems(prev => prev.map(item => 
          item.id === itemId ? { ...item, liked: currentItem.liked } : item
        ));
        console.error('Errore nell\'aggiornamento preferiti:', result.error);
      }
    } catch (error) {
      console.error('Errore nel toggle like:', error);
      // Ripristina lo stato in caso di errore
      setItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, liked: !item.liked } : item
      ));
    }
  };

  // Aggiorna la funzione deleteItem
  const deleteItem = async (itemId) => {
    try {
      // Conferma eliminazione
      if (!window.confirm('Sei sicuro di voler eliminare questa immagine? Questa azione non può essere annullata.')) {
        return;
      }
  
      // Rimuovi immediatamente dall'UI (optimistic update)
      const originalItems = [...items];
      setItems(prev => prev.filter(item => item.id !== itemId));
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
  
      // Chiama l'API per eliminare dal database
      const result = await deleteUserImage(itemId);
      
      if (!result.success) {
        // Se l'API fallisce, ripristina lo stato precedente
        setItems(originalItems);
        console.error('Errore nell\'eliminazione:', result.error);
        alert('Errore nell\'eliminazione dell\'immagine. Riprova più tardi.');
      }
    } catch (error) {
      console.error('Errore nel delete item:', error);
      // Ripristina lo stato in caso di errore
      loadUserImages(); // Ricarica tutto per essere sicuri
      alert('Errore nell\'eliminazione dell\'immagine. Riprova più tardi.');
    }
  };

  // Aggiorna la funzione deleteSelected per eliminazioni multiple
  const deleteSelected = async () => {
    if (selectedItems.size === 0) return;
  
    const count = selectedItems.size;
    if (!window.confirm(`Sei sicuro di voler eliminare ${count} immagini? Questa azione non può essere annullata.`)) {
      return;
    }
  
    try {
      // Converti Set in Array per iterare
      const itemsToDelete = Array.from(selectedItems);
  
      // Rimuovi immediatamente dall'UI
      const originalItems = [...items];
      setItems(prev => prev.filter(item => !selectedItems.has(item.id)));
      setSelectedItems(new Set());
  
      // Elimina tutte le immagini selezionate
      const deletePromises = itemsToDelete.map(itemId => deleteUserImage(itemId));
      const results = await Promise.allSettled(deletePromises);
  
      // Controlla se ci sono stati errori
      const failedDeletes = results.filter(result => 
        result.status === 'rejected' || !result.value?.success
      );
  
      if (failedDeletes.length > 0) {
        console.error('Alcuni elementi non sono stati eliminati:', failedDeletes);
        // Ricarica i dati per sincronizzare lo stato
        loadUserImages();
        alert(`${failedDeletes.length} immagini non sono state eliminate. Ricaricando la galleria...`);
      }
    } catch (error) {
      console.error('Errore nell\'eliminazione multipla:', error);
      // Ricarica tutto in caso di errore
      loadUserImages();
      alert('Errore nell\'eliminazione delle immagini. Ricaricando la galleria...');
    }
  };

  const downloadItem = async (item) => {
    try {
      const response = await fetch(item.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const extension = item.type === 'video' ? 'mp4' : 'jpg';
      downloadFile(url, `gallery-${item.id}.${extension}`);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Errore nel download:', error);
    }
  };

  const copyItemUrl = async (item) => {
    try {
      await copyToClipboard(item.url);
    } catch (error) {
      console.error('Errore nella copia:', error);
    }
  };

  const shareItem = async (item) => {
    try {
      await shareContent({
        title: `Creazione AI: ${item.prompt.substring(0, 50)}...`,
        url: item.url
      });
    } catch (error) {
      console.error('Errore nella condivisione:', error);
      copyItemUrl(item);
    }
  };

  const toggleVideoPlay = (itemId) => {
    const video = document.getElementById(`video-${itemId}`);
    if (video) {
      if (playingVideos.has(itemId)) {
        video.pause();
        setPlayingVideos(prev => {
          const newSet = new Set(prev);
          newSet.delete(itemId);
          return newSet;
        });
      } else {
        video.play();
        setPlayingVideos(prev => new Set([...prev, itemId]));
      }
    }
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    setSelectedItems(new Set(filteredItems.map(item => item.id)));
  };

  const deselectAll = () => {
    setSelectedItems(new Set());
  };

  

  

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-400 text-xl mb-4">{error}</div>
          <button 
            onClick={loadUserImages}
            className="generate-btn px-6 py-3 rounded-xl text-sm"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8">
        {/* Header con Pill e Statistiche compattate */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div className="flex items-center gap-4 flex-wrap">
            <h1 className="text-4xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.02em' }}>
              La Tua Galleria
            </h1>
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full glass border-amber-500/20 bg-amber-500/5 text-amber-200/80 text-[11px] font-semibold"
            >
              <AlertCircle size={14} className="text-amber-500" />
              <span>Conservazione: 7 giorni</span>
            </motion.div>
          </div>
          
          {/* Statistiche orizzontali compatte */}
          <div className="flex items-center gap-6 overflow-x-auto no-scrollbar pb-2 md:pb-0">
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-white tabular-nums leading-tight">{items.length}</span>
              <span className="label-sm !text-[9px] !tracking-widest opacity-40 uppercase">Totali</span>
            </div>
            <div className="w-px h-8 bg-white/10 hidden sm:block" />
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-white tabular-nums leading-tight">{filteredItems.length}</span>
              <span className="label-sm !text-[9px] !tracking-widest opacity-40 uppercase">Filtrati</span>
            </div>
            <div className="w-px h-8 bg-white/10 hidden sm:block" />
            <div className="flex flex-col">
              <span className="text-2xl font-bold text-white tabular-nums leading-tight">{items.filter(item => item.liked).length}</span>
              <span className="label-sm !text-[9px] !tracking-widest opacity-40 uppercase">Preferiti</span>
            </div>
          </div>
        </div>

        {/* Toolbar: Filtri, Ricerca e View Mode */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="panel rounded-[28px] p-4 mb-8"
        >
          <div className="flex flex-col lg:flex-row items-center gap-4">
            {/* Ricerca e View Toggle */}
            <div className="flex items-center gap-3 w-full lg:w-auto flex-1">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/20" size={16} />
                <input
                  type="text"
                  placeholder="Cerca per prompt o modello..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-field w-full pl-11 pr-4 py-2.5 rounded-2xl text-sm"
                />
              </div>

              <div className="flex items-center p-1 bg-white/5 rounded-xl border border-white/10 flex-shrink-0">
                <button
                  onClick={() => setViewMode('grid')}
                  className={cn(
                    "p-2 rounded-lg transition-all",
                    viewMode === 'grid' ? "bg-white/10 text-white shadow-sm" : "text-white/30 hover:text-white/60"
                  )}
                >
                  <Grid3X3 size={18} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={cn(
                    "p-2 rounded-lg transition-all",
                    viewMode === 'list' ? "bg-white/10 text-white shadow-sm" : "text-white/30 hover:text-white/60"
                  )}
                >
                  <List size={18} />
                </button>
              </div>
            </div>

            {/* Filtri Tipo e Ordinamento */}
            <div className="flex items-center gap-3 w-full lg:w-auto overflow-x-auto no-scrollbar">
              <div className="flex gap-1 p-1 bg-white/5 rounded-[18px] border border-white/10 flex-shrink-0">
                {filterOptions.map((filter) => {
                  const Icon = filter.icon;
                  const isActive = selectedFilter === filter.id;
                  return (
                    <button
                      key={filter.id}
                      onClick={() => setSelectedFilter(filter.id)}
                      className={cn(
                        "px-4 py-2 rounded-xl text-xs font-bold transition-all duration-300 flex items-center gap-2",
                        isActive
                          ? "bg-white text-black shadow-lg scale-[1.02]"
                          : "text-white/50 hover:text-white/80 hover:bg-white/5"
                      )}
                    >
                      <Icon size={14} />
                      {filter.name}
                    </button>
                  );
                })}
              </div>

              <div className="relative min-w-[140px] flex-shrink-0">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="input-field appearance-none w-full pl-4 pr-10 py-2.5 rounded-xl text-xs font-bold"
                >
                  {sortOptions.map(option => (
                    <option key={option.id} value={option.id} className="bg-[#0a0a0c]">
                      {option.name}
                    </option>
                  ))}
                </select>
                <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-white/20" size={12} />
              </div>
            </div>
          </div>

          {/* Azioni di massa */}
          <AnimatePresence>
            {selectedItems.size > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 pt-4 border-t border-white/5"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2 px-3 py-1 bg-white/10 rounded-full border border-white/10">
                      <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                      <span className="text-[11px] font-bold text-white/90">
                        {selectedItems.size} selezionati
                      </span>
                    </div>
                    <div className="flex gap-4">
                      <button onClick={selectAll} className="text-[11px] font-bold text-white/60 hover:text-white transition-colors">
                        Seleziona tutti
                      </button>
                      <button onClick={deselectAll} className="text-[11px] font-bold text-white/30 hover:text-white/60 transition-colors">
                        Deseleziona
                      </button>
                    </div>
                  </div>
                  <motion.button
                    onClick={deleteSelected}
                    className="px-5 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 border border-red-500/20 rounded-xl transition-all flex items-center gap-2 text-xs font-bold"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Trash2 size={14} />
                    Elimina Selezione
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Griglia/Lista Galleria */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {loading ? (
            // Spinner solo nella sezione delle immagini
            <div className="flex items-center justify-center py-16">
              <div className="text-center">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-16 h-16 mx-auto mb-4"
                >
                  <RefreshCw size={48} style={{ color: 'rgba(255,255,255,0.55)' }} />
                </motion.div>
                <h3 className="text-xl font-semibold text-white mb-2">Caricamento Immagini</h3>
                <p style={{ color: 'rgba(255,255,255,0.38)' }}>Stiamo recuperando le tue creazioni...</p>
              </div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-16">
              <div className="icon-box w-24 h-24 mx-auto mb-6 rounded-3xl">
                <Search size={40} style={{ color: 'rgba(255,255,255,0.45)' }} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">Nessun elemento trovato</h3>
              <p className="max-w-sm mx-auto" style={{ color: 'rgba(255,255,255,0.38)' }}>
                {items.length === 0
                  ? "Inizia a generare immagini per vederle qui!"
                  : "Prova a modificare i filtri di ricerca."}
              </p>
              {items.length === 0 && (
                <a
                  href="/generate"
                  className="generate-btn inline-flex items-center gap-2 px-6 py-3 rounded-xl mt-4 text-sm"
                >
                  <ImageIcon size={20} />
                  Genera la tua prima immagine
                </a>
              )}
            </div>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              <AnimatePresence mode="popLayout">
                {filteredItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="group relative"
                  >
                    <div className="panel rounded-2xl overflow-hidden transition-all duration-300 hover:border-white/20">
                      {/* Checkbox selezione */}
                      <div className="absolute top-3 left-3 z-10">
                        <motion.button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleItemSelection(item.id);
                          }}
                          className={cn(
                            "w-6 h-6 rounded-lg border-2 transition-all duration-300 flex items-center justify-center",
                            selectedItems.has(item.id)
                              ? "bg-white border-white text-black"
                              : "border-white/30 hover:border-white/60"
                          )}
                          style={!selectedItems.has(item.id) ? { background: 'rgba(0,0,0,0.5)' } : {}}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          {selectedItems.has(item.id) && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className="text-xs"
                            >
                              ✓
                            </motion.div>
                          )}
                        </motion.button>
                      </div>

                      {/* Media */}
                      <div className="relative aspect-[4/3] overflow-hidden cursor-pointer" onClick={() => setSelectedItem(item)}>
                        {item.type === 'image' ? (
                          <img
                            src={item.thumbnail}
                            alt={item.prompt}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <div className="relative w-full h-full">
                            <video
                              id={`video-${item.id}`}
                              src={item.url}
                              className="w-full h-full object-cover"
                              loop
                              muted
                              playsInline
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <motion.button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleVideoPlay(item.id);
                                }}
                                className="p-3 bg-black/50 backdrop-blur-md rounded-full hover:bg-black/70 transition-colors text-white"
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                              >
                                {playingVideos.has(item.id) ? <Pause size={24} /> : <Play size={24} />}
                              </motion.button>
                            </div>
                          </div>
                        )}

                        {/* Badge tipo */}
                        <div className="absolute top-3 right-3">
                          <span className={cn(
                            "px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1",
                            "pill-ghost text-xs"
                          )}>
                            {item.type === 'image' ? <ImageIcon size={12} /> : <Video size={12} />}
                            {item.type === 'image' ? 'IMG' : 'VID'}
                          </span>
                        </div>

                        {/* Pulsanti azione */}
                        <div className="absolute bottom-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <motion.button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleLike(item.id);
                            }}
                            className={cn(
                              "p-2 rounded-xl backdrop-blur-md transition-colors",
                              item.liked 
                                ? "bg-red-500/50 text-red-300" 
                                : "bg-black/50 text-white hover:bg-black/70"
                            )}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Heart size={16} fill={item.liked ? 'currentColor' : 'none'} />
                          </motion.button>
                          <motion.button
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadItem(item);
                            }}
                            className="p-2 bg-black/50 backdrop-blur-md rounded-xl hover:bg-black/70 transition-colors text-white"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                          >
                            <Download size={16} />
                          </motion.button>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-medium text-white text-sm line-clamp-2 flex-1">
                            {item.prompt}
                          </h3>
                          <div className="flex items-center gap-1 ml-2" style={{ color: 'rgba(255,255,255,0.55)' }}>
                            <Star size={12} fill="currentColor" />
                            <span className="text-xs">{item.rating}</span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs" style={{ color: 'rgba(255,255,255,0.38)' }}>
                          <span>{item.model}</span>
                          <span>{formatDate(new Date(item.timestamp))}</span>
                        </div>
                        <div className="flex items-center justify-between text-xs mt-1" style={{ color: 'rgba(255,255,255,0.28)' }}>
                          <span>{item.dimensions}</span>
                          <span>{item.size}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          ) : (
            <div className="space-y-4">
              <AnimatePresence mode="popLayout">
                {filteredItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, delay: index * 0.02 }}
                    className="panel rounded-2xl p-4 transition-all duration-300 hover:border-white/20"
                  >
                    <div className="flex items-center gap-4">
                      {/* Selezione */}
                      <motion.button
                        onClick={() => toggleItemSelection(item.id)}
                        className={cn(
                          "w-6 h-6 rounded-lg border-2 transition-all duration-300 flex items-center justify-center flex-shrink-0",
                          selectedItems.has(item.id)
                            ? "bg-white border-white text-black"
                            : "border-white/30 hover:border-white/60"
                        )}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        {selectedItems.has(item.id) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="text-xs"
                          >
                            ✓
                          </motion.div>
                        )}
                      </motion.button>

                      {/* Thumbnail */}
                      <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 cursor-pointer" onClick={() => setSelectedItem(item)}>
                        {item.type === 'image' ? (
                          <img
                            src={item.thumbnail}
                            alt={item.prompt}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="relative w-full h-full">
                            <video
                              src={item.url}
                              className="w-full h-full object-cover"
                              muted
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <Play size={16} className="text-white" />
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-1">
                          <h3 className="font-medium text-white text-sm line-clamp-1 flex-1">
                            {item.prompt}
                          </h3>
                          <div className="hidden sm:flex items-center gap-1 ml-2" style={{ color: 'rgba(255,255,255,0.55)' }}>
                            <Star size={12} fill="currentColor" />
                            <span className="text-xs">{item.rating}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-4 text-xs" style={{ color: 'rgba(255,255,255,0.38)' }}>
                          <span className="flex items-center gap-1 truncate max-w-[100px] sm:max-w-none">
                            {item.type === 'image' ? <ImageIcon size={12} className="flex-shrink-0" /> : <Video size={12} className="flex-shrink-0" />}
                            <span className="truncate">{item.model}</span>
                          </span>
                          <span className="hidden sm:flex items-center gap-1">
                            <Clock size={12} />
                            {formatDate(new Date(item.timestamp))}
                          </span>
                          <span className="hidden md:block">{item.dimensions}</span>
                          <span className="hidden md:block">{item.size}</span>
                        </div>
                      </div>

                      {/* Azioni */}
                      <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                        <motion.button
                          onClick={() => toggleLike(item.id)}
                          className={cn(
                            "p-2 rounded-xl transition-colors",
                            item.liked
                              ? "text-red-400" : "pill-ghost"
                          )}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Heart size={16} fill={item.liked ? 'currentColor' : 'none'} />
                        </motion.button>
                        <motion.button
                          onClick={() => downloadItem(item)}
                          className="pill-ghost p-2 rounded-xl"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Download size={16} />
                        </motion.button>
                        <motion.button
                          onClick={() => copyItemUrl(item)}
                          className="hidden sm:flex pill-ghost p-2 rounded-xl"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Copy size={16} />
                        </motion.button>
                        <motion.button
                          onClick={() => shareItem(item)}
                          className="hidden sm:flex pill-ghost p-2 rounded-xl"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Share2 size={16} />
                        </motion.button>
                        <motion.button
                          onClick={() => deleteItem(item.id)}
                          className="pill-ghost p-2 rounded-xl hover:text-red-400"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Trash2 size={16} />
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </motion.div>

        {/* Modal fullscreen */}
        <AnimatePresence>
          {selectedItem && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => setSelectedItem(null)}
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.8, opacity: 0 }}
                className="relative w-full max-w-4xl rounded-2xl sm:rounded-3xl overflow-hidden" style={{ background: 'rgba(8,8,12,0.97)', backdropFilter: 'blur(32px)', border: '1px solid rgba(255,255,255,0.10)', boxShadow: '0 40px 120px rgba(0,0,0,0.8)' }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Media */}
                <div className="relative">
                  {selectedItem.type === 'image' ? (
                    <img
                      src={selectedItem.url}
                      alt={selectedItem.prompt}
                      className="w-full max-h-[60vh] sm:max-h-[70vh] object-contain"
                    />
                  ) : (
                    <video
                      src={selectedItem.url}
                      className="w-full max-h-[60vh] sm:max-h-[70vh] object-contain"
                      controls
                      autoPlay
                      loop
                    />
                  )}

                  {/* Pulsante chiudi — touch-friendly */}
                  <button
                    onClick={() => setSelectedItem(null)}
                    className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center bg-black/60 backdrop-blur-md rounded-xl text-white hover:bg-black/80 transition-colors"
                  >
                    ✕
                  </button>

                  {/* Pulsanti azione */}
                  <div className="absolute bottom-3 right-3 flex gap-1.5 sm:gap-2">
                    <motion.button
                      onClick={() => toggleLike(selectedItem.id)}
                      className={cn(
                        "p-2.5 sm:p-3 rounded-xl backdrop-blur-md transition-colors",
                        selectedItem.liked
                          ? "bg-red-500/50 text-red-300"
                          : "bg-black/50 text-white hover:bg-black/70"
                      )}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Heart size={18} fill={selectedItem.liked ? 'currentColor' : 'none'} />
                    </motion.button>
                    <motion.button
                      onClick={() => downloadItem(selectedItem)}
                      className="p-2.5 sm:p-3 bg-black/50 backdrop-blur-md rounded-xl hover:bg-black/70 transition-colors text-white"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Download size={18} />
                    </motion.button>
                    <motion.button
                      onClick={() => copyItemUrl(selectedItem)}
                      className="hidden sm:flex p-3 bg-black/50 backdrop-blur-md rounded-xl hover:bg-black/70 transition-colors text-white"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Copy size={18} />
                    </motion.button>
                    <motion.button
                      onClick={() => shareItem(selectedItem)}
                      className="p-2.5 sm:p-3 bg-black/50 backdrop-blur-md rounded-xl hover:bg-black/70 transition-colors text-white"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Share2 size={18} />
                    </motion.button>
                  </div>
                </div>

                {/* Pannello info */}
                <div className="p-4 sm:p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-base sm:text-xl font-bold text-white mb-2 line-clamp-2">{selectedItem.prompt}</h2>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-400">
                        <span className="flex items-center gap-1 truncate max-w-[140px] sm:max-w-none">
                          {selectedItem.type === 'image' ? <ImageIcon size={13} className="flex-shrink-0" /> : <Video size={13} className="flex-shrink-0" />}
                          <span className="truncate">{selectedItem.model}</span>
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar size={13} />
                          {formatDate(new Date(selectedItem.timestamp))}
                        </span>
                        <span className="hidden sm:block">{selectedItem.dimensions}</span>
                        <span className="hidden sm:block">{selectedItem.size}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-yellow-400 flex-shrink-0">
                      <Star size={14} fill="currentColor" />
                      <span className="font-medium text-sm">{selectedItem.rating}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}